/**
 * useKeyboardNavigationV2 - Keyboard shortcuts for v2 Challenge UI
 *
 * Shortcuts:
 * - 1-4: Select answer A-D
 * - Ctrl+1-4: Cross out A-D
 * - Shift+1-5: Set confidence
 * - Enter: Next question
 * - Shift+Enter: Previous question
 * - Arrow Left/Right: Navigate prev/next
 * - M: Mark for review
 * - Ctrl+R: Open review modal
 * - Ctrl+F: Toggle focus mode
 * - P: Toggle pause
 * - Ctrl+Enter: Submit stage
 * - Escape: Exit focus mode / Close modal / Resume from pause
 */

import { useEffect, useCallback } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectIsMCQ,
  selectHasOpenModal
} from '../../hooks/useChallenge'

export const useKeyboardNavigationV2 = () => {
  // Store state
  const isMCQ = useChallengeStoreV2(selectIsMCQ)
  const hasOpenModal = useChallengeStoreV2(selectHasOpenModal)
  const isFocusMode = useChallengeStoreV2((state) => state.isFocusMode)
  const isPaused = useChallengeStoreV2((state) => state.isPaused)

  // Store actions
  const setSelectedAnswer = useChallengeStoreV2((state) => state.setSelectedAnswer)
  const toggleCrossOut = useChallengeStoreV2((state) => state.toggleCrossOut)
  const setConfidence = useChallengeStoreV2((state) => state.setConfidence)
  const toggleMarkedForReview = useChallengeStoreV2((state) => state.toggleMarkedForReview)
  const handleNavigatePrevious = useChallengeStoreV2((state) => state.handleNavigatePrevious)
  const handleNavigateNext = useChallengeStoreV2((state) => state.handleNavigateNext)
  const handleSubmitStage = useChallengeStoreV2((state) => state.handleSubmitStage)
  const openReviewModal = useChallengeStoreV2((state) => state.openReviewModal)
  const closeAllModals = useChallengeStoreV2((state) => state.closeAllModals)
  const toggleFocusMode = useChallengeStoreV2((state) => state.toggleFocusMode)
  const exitFocusMode = useChallengeStoreV2((state) => state.exitFocusMode)
  const togglePause = useChallengeStoreV2((state) => state.togglePause)
  const resumeChallenge = useChallengeStoreV2((state) => state.resumeChallenge)
  const toggleKeyboardHints = useChallengeStoreV2((state) => state.toggleKeyboardHints)

  // Handle keydown events
  const handleKeyDown = useCallback(
    (event) => {
      const { key, ctrlKey, altKey, shiftKey, metaKey } = event

      // Don't capture if user is typing in an input
      const target = event.target
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        // Allow Escape to work even in inputs
        if (key !== 'Escape') {
          return
        }
      }

      // Escape - close modals, exit focus mode, or resume from pause
      if (key === 'Escape') {
        event.preventDefault()
        if (isPaused) {
          resumeChallenge()
        } else if (hasOpenModal) {
          closeAllModals()
        } else if (isFocusMode) {
          exitFocusMode()
        }
        return
      }

      // If paused, only Escape and P work (to resume)
      if (isPaused) {
        if (key === 'p' || key === 'P') {
          event.preventDefault()
          togglePause()
        }
        return
      }

      // If modal is open, only Escape works
      if (hasOpenModal) {
        return
      }

      // Alt combinations (check first, before Ctrl)
      if (altKey && !ctrlKey && !metaKey && !shiftKey) {
        // Alt+Enter - previous question
        if (key === 'Enter') {
          event.preventDefault()
          handleNavigatePrevious()
          return
        }
      }

      // Ctrl/Cmd combinations (no Alt)
      if ((ctrlKey || metaKey) && !altKey) {
        // Ctrl+Enter - submit stage
        if (key === 'Enter') {
          event.preventDefault()
          handleSubmitStage()
          return
        }

        // Ctrl+1-4 - cross out answers (MCQ only)
        if (isMCQ && key >= '1' && key <= '4') {
          event.preventDefault()
          const answerMap = { 1: 'A', 2: 'B', 3: 'C', 4: 'D' }
          toggleCrossOut(answerMap[key])
          return
        }

        // Ctrl+R - review modal
        if (key === 'r' || key === 'R') {
          event.preventDefault()
          openReviewModal()
          return
        }

        // Ctrl+F - focus mode
        if (key === 'f' || key === 'F') {
          event.preventDefault()
          toggleFocusMode()
          return
        }

        return
      }

      // Shift combinations
      if (shiftKey && !altKey && !ctrlKey && !metaKey) {
        // Shift+1-5 - confidence levels
        // With Shift, key becomes symbol (!, @, #, $, %) - check event.code instead
        const shiftNumberMap = {
          '!': 1,
          '@': 2,
          '#': 3,
          $: 4,
          '%': 5,
          1: 1,
          2: 2,
          3: 3,
          4: 4,
          5: 5
        }
        const codeNumberMap = {
          Digit1: 1,
          Digit2: 2,
          Digit3: 3,
          Digit4: 4,
          Digit5: 5
        }
        const confidenceLevel = shiftNumberMap[key] || codeNumberMap[event.code]

        if (confidenceLevel) {
          event.preventDefault()
          setConfidence(confidenceLevel)
          return
        }
        return
      }

      // Plain keys (no modifiers)
      if (!ctrlKey && !altKey && !shiftKey && !metaKey) {
        // Enter - next question
        if (key === 'Enter') {
          event.preventDefault()
          handleNavigateNext()
          return
        }

        // Number keys 1-4 - select answer (MCQ only)
        if (isMCQ && key >= '1' && key <= '4') {
          event.preventDefault()
          const answerMap = { 1: 'A', 2: 'B', 3: 'C', 4: 'D' }
          setSelectedAnswer(answerMap[key])
          return
        }

        // Arrow keys - navigation
        if (key === 'ArrowRight' || key === 'n') {
          event.preventDefault()
          handleNavigateNext()
          return
        }

        if (key === 'ArrowLeft') {
          event.preventDefault()
          handleNavigatePrevious()
          return
        }

        // P - toggle pause
        if (key === 'p' || key === 'P') {
          event.preventDefault()
          togglePause()
          return
        }

        // M - mark for review
        if (key === 'm' || key === 'M') {
          event.preventDefault()
          toggleMarkedForReview()
          return
        }

        // ? - show keyboard shortcuts
        if (key === '?') {
          event.preventDefault()
          toggleKeyboardHints()
          return
        }
      }
    },
    [
      isMCQ,
      hasOpenModal,
      isFocusMode,
      isPaused,
      setSelectedAnswer,
      toggleCrossOut,
      setConfidence,
      toggleMarkedForReview,
      handleNavigatePrevious,
      handleNavigateNext,
      handleSubmitStage,
      openReviewModal,
      closeAllModals,
      toggleFocusMode,
      exitFocusMode,
      togglePause,
      resumeChallenge,
      toggleKeyboardHints
    ]
  )

  // Add event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  // Return shortcuts info for display
  return {
    shortcuts: {
      answer: '1-4',
      crossOut: 'Ctrl+1-4',
      confidence: 'Shift+1-5',
      previous: 'Shift+Enter / ←',
      next: 'Enter / → / N',
      mark: 'M',
      review: 'Ctrl+R',
      focus: 'Ctrl+F',
      pause: 'P',
      submit: 'Ctrl+Enter',
      close: 'Esc'
    }
  }
}

export default useKeyboardNavigationV2
